import React from "react";
import { Link } from "react-router-dom";
import {
  Check, Phone, ArrowRight, MapPin, Smartphone, CloudHail, BadgeCheck,
  Star, ShieldCheck, Award, Clock,
} from "lucide-react";
import Hero from "../components/Hero";
import Seo from "../components/Seo";
import { Reveal, SectionHeading } from "../components/Shared";
import { features, services, testimonials, business } from "../mock";
import { serviceLinks } from "../data/servicePages";

const iconMap = { MapPin, Smartphone, CloudHail, BadgeCheck };

const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Dentcraft NSW",
  url: "https://dentcraftnsw.au/",
  publisher: { "@type": "AutoRepair", name: "Dentcraft NSW", "@id": "https://dentcraftnsw.au/#business" },
};

const Home = () => {
  return (
    <div>
      <Seo
        title="Paintless Dent Removal Sydney | Dentcraft NSW"
        description="Professional paintless dent removal in Sydney and Western Sydney. Mobile dent repairs for door dings, hail damage and vehicle dents. Get a free quote from Dentcraft NSW."
        path="/"
        schema={HOME_SCHEMA}
      />
      <Hero />

      {/* Intro + features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <span className="text-[#84bd00] font-semibold text-sm tracking-widest uppercase">Paintless Dent Removal Sydney</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#242424] leading-tight">
                Your mobile paintless dent removal specialists in Western Sydney
              </h2>
              <p className="mt-5 text-gray-600 text-lg leading-relaxed">
                Dentcraft NSW are your mobile, on-site paintless dent removal (PDR) repairers across Western Sydney and Sydney Metro — for your car, truck or motorbike. From door dings to hail damage, we remove dents without repainting your vehicle.
              </p>
              <p className="mt-4 text-[#242424] font-semibold">
                Contact {business.owner.split(" ")[0]} on {business.phone} for a free estimate to repair your damaged car.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href={`tel:${business.phoneRaw}`} className="inline-flex items-center gap-2 bg-[#84bd00] hover:bg-[#75a800] text-white font-semibold px-6 py-3 rounded-lg transition-colors">
                  <Phone size={18} /> Call for a Free Estimate
                </a>
                <Link to="/our-services" className="inline-flex items-center gap-2 border border-gray-300 hover:border-[#84bd00] text-[#242424] font-semibold px-6 py-3 rounded-lg transition-colors">
                  Our Services <ArrowRight size={18} />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#f7f9f2] hover:bg-[#eef4e0] p-4 rounded-xl transition-colors border border-transparent hover:border-[#84bd00]/30">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-[#84bd00] flex items-center justify-center mt-0.5">
                      <Check size={16} className="text-white" />
                    </div>
                    <span className="text-sm font-medium text-[#3a3a3a] leading-snug">{f}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* About Sam banner */}
      <section className="py-20 bg-[#242424] relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-96 h-96 bg-[#84bd00]/10 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <Reveal>
            <span className="text-[#84bd00] font-semibold text-sm tracking-widest uppercase">Meet the Expert</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-white">{business.owner}</h2>
            <p className="mt-6 text-gray-300 text-lg leading-relaxed">
              Sam offers an exclusive professional mobile paintless dent repair and ding removal service. His wealth of panel repair knowledge and PDR experience ensures your car looks good as new, keeping that factory finish. A fully licensed professional trained in both Germany and Australia, Sam has worked post hail-storm seasons throughout Sydney, Perth, Melbourne and New Zealand.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { icon: Award, label: "Trained in Germany & AU" },
                { icon: ShieldCheck, label: "Fully Licensed & Insured" },
                { icon: Clock, label: "Same-Day Service" },
              ].map((b, i) => (
                <div key={i} className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-[#84bd00]/15 border border-[#84bd00]/30 flex items-center justify-center">
                    <b.icon size={26} className="text-[#84bd00]" />
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{b.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services preview */}
      <section className="py-20 bg-[#f7f9f2]">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Dent Removal Sydney"
            title="Mobile dent repair services across Sydney"
            subtitle="Paintless dent removal can take only minutes compared with conventional body shop repairs. We remove dents caused by hail, door dings and shopping trolleys across Western Sydney and Sydney Metro."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => {
              const Icon = iconMap[s.icon];
              return (
                <Reveal key={s.id} delay={i * 100}>
                  <div className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
                    <div className="relative h-44 overflow-hidden">
                      <img src={s.image} alt={`${s.title} \u2013 Dentcraft NSW dent repair Sydney`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-3 left-3 w-11 h-11 rounded-xl bg-[#84bd00] flex items-center justify-center shadow-lg">
                        {Icon && <Icon size={22} className="text-white" />}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display font-bold text-lg text-[#242424] mb-2">{s.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{s.short}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            {serviceLinks.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-[#84bd00] text-[#242424] font-semibold px-5 py-2.5 rounded-lg transition-colors"
              >
                {l.label} <ArrowRight size={16} className="text-[#84bd00]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="100% Satisfaction" title="What our customers say" />
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="bg-[#f7f9f2] rounded-2xl p-7 h-full border border-transparent hover:border-[#84bd00]/30 transition-colors">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} size={18} className="text-[#84bd00] fill-[#84bd00]" />
                    ))}
                  </div>
                  <p className="text-gray-600 leading-relaxed italic">“{t.text}”</p>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-[#84bd00] text-white flex items-center justify-center font-bold">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-[#242424] text-sm">{t.name}</div>
                      <div className="text-gray-500 text-xs">{t.location}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CtaBanner />
    </div>
  );
};

export const CtaBanner = () => (
  <section className="py-16 bg-[#84bd00]">
    <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
      <div>
        <h3 className="font-display font-extrabold text-2xl md:text-3xl text-white">Ready to restore your factory finish?</h3>
        <p className="mt-2 text-white/90 text-lg">Take a photo of your dent and text Sam for an on-the-spot estimate.</p>
      </div>
      <a href={`tel:${business.phoneRaw}`} className="shrink-0 inline-flex items-center gap-2 bg-white text-[#242424] hover:bg-[#242424] hover:text-white font-bold px-8 py-4 rounded-lg transition-colors">
        <Phone size={20} /> {business.phone}
      </a>
    </div>
  </section>
);

export default Home;
